// Imposter Word Game Logic
// A hidden roles game where one player doesn't know the secret word

export type PlayerId = string;

export interface ImposterSettings {
  roundsBeforeVote: number; // Number of clue rounds before voting (default: 3)
  allowPhrases: boolean; // Allow short phrases instead of just one word
  multipleImposters: boolean; // Enable multiple imposters for larger groups
  category: string | null; // Optional category restriction
}

export const DEFAULT_IMPOSTER_SETTINGS: ImposterSettings = {
  roundsBeforeVote: 3,
  allowPhrases: false,
  multipleImposters: false,
  category: null,
};

export type GamePhase = 'lobby' | 'dealing' | 'clues' | 'voting' | 'reveal' | 'finished';

export interface PlayerClue {
  playerId: PlayerId;
  clue: string;
  roundNumber: number;
  timestamp: number;
}

export interface Vote {
  voterId: PlayerId;
  suspectId: PlayerId;
  timestamp: number;
}

export interface ImposterSnapshot {
  players: PlayerId[];
  imposterIds: PlayerId[]; // Support multiple imposters
  secretWord: string;
  category: string;
  phase: GamePhase;
  currentRound: number;
  clues: PlayerClue[];
  votes: Vote[];
  revealedImposters: boolean;
  imposterGuess: string | null; // The imposter's guess for the secret word
  scores: Record<PlayerId, number>; // Track scores across rounds
  roundsPlayed: number;
  currentPlayerIndex: number; // For turn-based clue giving
  settings: ImposterSettings;
}

export interface ImposterStateDocument extends ImposterSnapshot {
  partyId: string;
  createdAt: number;
  updatedAt: number;
}

// Word categories organized by difficulty
export const WORD_CATEGORIES = {
  food: {
    easy: ['Pizza', 'Apple', 'Bread', 'Cake', 'Ice Cream', 'Banana', 'Sandwich', 'Milk', 'Cookie', 'Cheese'],
    medium: ['Spaghetti', 'Hamburger', 'Popcorn', 'Salad', 'Muffin', 'Taco', 'Yogurt', 'Cereal', 'Peanut Butter', 'Chicken'],
    hard: ['Croissant', 'Quiche', 'Sushi', 'Ravioli', 'Guacamole', 'Eggplant', 'Avocado', 'Falafel', 'Pomegranate', 'Wasabi'],
  },
  animals: {
    easy: ['Dog', 'Cat', 'Cow', 'Fish', 'Horse', 'Duck', 'Bird', 'Pig', 'Rabbit', 'Lion'],
    medium: ['Elephant', 'Giraffe', 'Kangaroo', 'Panda', 'Penguin', 'Zebra', 'Bear', 'Monkey', 'Snake', 'Dolphin'],
    hard: ['Armadillo', 'Platypus', 'Narwhal', 'Chameleon', 'Wombat', 'Axolotl', 'Sloth', 'Tarantula', 'Iguana', 'Hedgehog'],
  },
  movies: {
    easy: ['Frozen', 'Toy Story', 'Spider-Man', 'Minions', 'Batman', 'Cars', 'Moana', 'Shrek', 'Harry Potter', 'Star Wars'],
    medium: ['Jurassic Park', 'Finding Nemo', 'Black Panther', 'Aladdin', 'Lion King', 'Home Alone', 'E.T.', 'Cinderella', 'Encanto', 'Up'],
    hard: ['Inception', 'Casablanca', 'The Godfather', 'Parasite', 'Interstellar', 'Schindler\'s List', 'Amélie', 'Jaws', 'Titanic', 'La La Land'],
  },
  sports: {
    easy: ['Soccer', 'Basketball', 'Baseball', 'Football', 'Tennis', 'Hockey', 'Golf', 'Swimming', 'Running', 'Volleyball'],
    medium: ['Badminton', 'Bowling', 'Rugby', 'Skateboarding', 'Surfing', 'Lacrosse', 'Karate', 'Gymnastics', 'Archery', 'Dodgeball'],
    hard: ['Polo', 'Curling', 'Fencing', 'Equestrian', 'Biathlon', 'Triathlon', 'Javelin', 'Bocce', 'Squash', 'Cricket'],
  },
  places: {
    easy: ['School', 'Park', 'Beach', 'Home', 'Zoo', 'Farm', 'Playground', 'Mall', 'Hospital', 'Library'],
    medium: ['City Hall', 'Stadium', 'Airport', 'Theater', 'Aquarium', 'Museum', 'Restaurant', 'Castle', 'Mountain', 'Bridge'],
    hard: ['Pyramids', 'Eiffel Tower', 'Great Wall', 'Taj Mahal', 'Colosseum', 'Machu Picchu', 'Stonehenge', 'Sydney Opera House', 'Statue of Liberty', 'Mount Everest'],
  },
  jobs: {
    easy: ['Teacher', 'Doctor', 'Chef', 'Farmer', 'Nurse', 'Firefighter', 'Police Officer', 'Singer', 'Athlete', 'Pilot'],
    medium: ['Librarian', 'Engineer', 'Lawyer', 'Dancer', 'Mechanic', 'Scientist', 'Actor', 'Author', 'Soldier', 'Baker'],
    hard: ['Archaeologist', 'Astronomer', 'Mathematician', 'Fashion Designer', 'Diplomat', 'Geologist', 'Biologist', 'Sculptor', 'Politician', 'Architect'],
  },
  objects: {
    easy: ['Ball', 'Chair', 'Book', 'Phone', 'Bed', 'Table', 'Shoes', 'Hat', 'Pen', 'Clock'],
    medium: ['Backpack', 'Camera', 'Guitar', 'Bicycle', 'Umbrella', 'Mirror', 'Radio', 'Blanket', 'Suitcase', 'Microwave'],
    hard: ['Telescope', 'Typewriter', 'Microscope', 'Projector', 'Compass', 'Thermometer', 'Accordion', 'Saxophone', 'Sewing Machine', 'Drone'],
  },
  vehicles: {
    easy: ['Car', 'Bus', 'Bike', 'Boat', 'Truck', 'Train', 'Plane', 'Taxi', 'Van', 'Scooter'],
    medium: ['Helicopter', 'Motorcycle', 'Sailboat', 'Tractor', 'Submarine', 'Jeep', 'Limousine', 'Hot Air Balloon', 'Skateboard', 'Rocket'],
    hard: ['Segway', 'Monorail', 'Rickshaw', 'Gondola', 'Hovercraft', 'Cable Car', 'Zeppelin', 'Tuk Tuk', 'Snowmobile', 'Amphibious Vehicle'],
  },
  holidays: {
    easy: ['Birthday', 'Christmas', 'Halloween', 'Easter', 'New Year', 'Thanksgiving', 'Wedding', 'Graduation', 'Valentine\'s Day', 'Party'],
    medium: ['Fireworks', 'Parade', 'Pumpkin', 'Santa Claus', 'Hanukkah', 'Costume', 'Cake', 'Gift', 'Balloon', 'Turkey'],
    hard: ['Piñata', 'Diwali', 'Ramadan', 'Kwanzaa', 'Lantern Festival', 'Oktoberfest', 'Mardi Gras', 'Passover', 'Holi', 'Cinco de Mayo'],
  },
  school: {
    easy: ['Teacher', 'Desk', 'Book', 'Pencil', 'Eraser', 'Notebook', 'Ruler', 'Backpack', 'Lunch', 'Bus'],
    medium: ['Calculator', 'Globe', 'Blackboard', 'Test', 'Science', 'History', 'Dictionary', 'Marker', 'Recess', 'Homework'],
    hard: ['Microscope', 'Thesis', 'Graduation', 'Laboratory', 'Debate', 'Scholarship', 'Periodic Table', 'Geometry', 'Physics', 'Biology'],
  },
  silly: {
    easy: ['Banana Peel', 'Fart', 'Unicorn', 'Slime', 'Chicken Nugget', 'Bubble', 'Robot', 'Pickle', 'Mustache', 'Toilet'],
    medium: ['Rubber Chicken', 'Disco Ball', 'Llama', 'Kazoo', 'Waffle', 'Flamingo', 'Donut', 'Pirate', 'Dinosaur Costume', 'Taco Truck'],
    hard: ['Whoopee Cushion', 'Platypus', 'Loch Ness Monster', 'Yeti', 'Marshmallow Cannon', 'Giant Rubber Duck', 'Sasquatch', 'Narwhal', 'UFO', 'Time Machine'],
  },
  fantasy: {
    easy: ['Dragon', 'Fairy', 'Wizard', 'Giant', 'Mermaid', 'Troll', 'Elf', 'Unicorn', 'Witch', 'Knight'],
    medium: ['Griffin', 'Phoenix', 'Centaur', 'Minotaur', 'Pegasus', 'Cyclops', 'Goblin', 'Genie', 'Werewolf', 'Vampire'],
    hard: ['Chimera', 'Kraken', 'Basilisk', 'Hydra', 'Leviathan', 'Banshee', 'Sphinx', 'Thunderbird', 'Golem', 'Djinn'],
  },
  technology: {
    easy: ['Phone', 'Laptop', 'TV', 'Headphones', 'Camera', 'Tablet', 'Mouse', 'Keyboard', 'Watch', 'Remote'],
    medium: ['Drone', 'Printer', 'Microphone', 'Projector', 'Smartwatch', 'Video Game', 'Calculator', 'Telescope', 'Robot', 'Flashlight'],
    hard: ['3D Printer', 'Virtual Reality', 'Quantum Computer', 'Satellite', 'Supercomputer', 'Nanobot', 'Hoverboard', 'AI Assistant', 'Cryptominer', 'Hologram'],
  },
  nature: {
    easy: ['Tree', 'Rock', 'River', 'Sun', 'Moon', 'Flower', 'Grass', 'Mountain', 'Cloud', 'Leaf'],
    medium: ['Volcano', 'Glacier', 'Canyon', 'Desert', 'Jungle', 'Waterfall', 'Ocean', 'Storm', 'Rainbow', 'Cave'],
    hard: ['Aurora Borealis', 'Tsunami', 'Earthquake', 'Meteor', 'Eclipse', 'Black Hole', 'Sandstorm', 'Tornado', 'Coral Reef', 'Fossil'],
  },
  music: {
    easy: ['Guitar', 'Piano', 'Song', 'Dance', 'Singer', 'Drum', 'Radio', 'Movie', 'Game', 'Stage'],
    medium: ['Violin', 'Trumpet', 'DJ', 'Orchestra', 'Actor', 'Musical', 'Karaoke', 'Popcorn', 'Audience', 'Costume'],
    hard: ['Didgeridoo', 'Harpsichord', 'Theremin', 'Sitar', 'Bagpipes', 'Sousaphone', 'Ballet', 'Opera', 'Mime', 'Shakespeare'],
  },
};

export class ImposterGame {
  private snapshot: ImposterSnapshot;

  constructor(
    players: PlayerId[],
    settings: Partial<ImposterSettings> = {}
  ) {
    if (players.length < 3) {
      throw new Error('Imposter game requires at least 3 players');
    }

    const finalSettings = { ...DEFAULT_IMPOSTER_SETTINGS, ...settings };
    
    // Determine number of imposters
    const imposterCount = finalSettings.multipleImposters && players.length >= 8 ? 2 : 1;
    
    // Randomly select imposters
    const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
    const imposterIds = shuffledPlayers.slice(0, imposterCount);

    // Select a random word
    const { word, category } = this.selectRandomWord(finalSettings.category);

    this.snapshot = {
      players,
      imposterIds,
      secretWord: word,
      category,
      phase: 'dealing',
      currentRound: 0,
      clues: [],
      votes: [],
      revealedImposters: false,
      imposterGuess: null,
      scores: players.reduce((acc, p) => ({ ...acc, [p]: 0 }), {}),
      roundsPlayed: 0,
      currentPlayerIndex: 0,
      settings: finalSettings,
    };
  }

  private selectRandomWord(categoryFilter: string | null): { word: string; category: string } {
    const categories = categoryFilter 
      ? [categoryFilter]
      : Object.keys(WORD_CATEGORIES);
    
    const category = categories[Math.floor(Math.random() * categories.length)];
    const difficulties = ['easy', 'medium', 'hard'];
    const difficulty = difficulties[Math.floor(Math.random() * difficulties.length)];
    
    const wordList = WORD_CATEGORIES[category as keyof typeof WORD_CATEGORIES][difficulty as keyof typeof WORD_CATEGORIES['food']];
    const word = wordList[Math.floor(Math.random() * wordList.length)];

    return { word, category };
  }

  // Get current player whose turn it is to give a clue
  getCurrentPlayer(): PlayerId {
    return this.snapshot.players[this.snapshot.currentPlayerIndex];
  }

  // Start the clue-giving phase
  startCluePhase(): void {
    if (this.snapshot.phase !== 'dealing') {
      throw new Error('Can only start clue phase from dealing phase');
    }
    this.snapshot.phase = 'clues';
    this.snapshot.currentRound = 1;
    this.snapshot.currentPlayerIndex = 0;
  }

  // Force start voting phase (host override)
  forceVoting(): void {
    if (this.snapshot.phase !== 'clues') {
      throw new Error('Can only force voting from clues phase');
    }
    this.snapshot.phase = 'voting';
  }

  // Force advance to next clue round (host override for stuck games)
  forceNextRound(): void {
    if (this.snapshot.phase !== 'clues') {
      throw new Error('Can only advance round during clues phase');
    }
    this.snapshot.currentRound++;
    this.snapshot.currentPlayerIndex = 0;
  }

  // Submit a clue from a player
  submitClue(playerId: PlayerId, clue: string): void {
    if (this.snapshot.phase !== 'clues') {
      throw new Error('Not in clue phase');
    }

    if (playerId !== this.getCurrentPlayer()) {
      throw new Error('Not this player\'s turn');
    }

    // Validate clue doesn't contain the secret word (for non-imposters)
    if (!this.snapshot.imposterIds.includes(playerId)) {
      const lowerClue = clue.toLowerCase();
      const lowerWord = this.snapshot.secretWord.toLowerCase();
      if (lowerClue.includes(lowerWord)) {
        throw new Error('Clue cannot contain the secret word');
      }
    }

    this.snapshot.clues.push({
      playerId,
      clue,
      roundNumber: this.snapshot.currentRound,
      timestamp: Date.now(),
    });

    // Move to next player
    this.snapshot.currentPlayerIndex++;

    // If all players have given clues this round
    if (this.snapshot.currentPlayerIndex >= this.snapshot.players.length) {
      this.snapshot.currentPlayerIndex = 0;
      
      // Check if we've completed enough rounds to vote
      if (this.snapshot.currentRound >= this.snapshot.settings.roundsBeforeVote) {
        this.snapshot.phase = 'voting';
      } else {
        this.snapshot.currentRound++;
      }
    }
  }

  // Submit a vote for who the imposter is
  submitVote(voterId: PlayerId, suspectId: PlayerId): void {
    if (this.snapshot.phase !== 'voting') {
      throw new Error('Not in voting phase');
    }

    if (!this.snapshot.players.includes(suspectId)) {
      throw new Error('Invalid suspect');
    }

    // Remove any existing vote from this voter
    this.snapshot.votes = this.snapshot.votes.filter(v => v.voterId !== voterId);

    this.snapshot.votes.push({
      voterId,
      suspectId,
      timestamp: Date.now(),
    });

    // If all players have voted, move to reveal
    if (this.snapshot.votes.length >= this.snapshot.players.length) {
      this.snapshot.phase = 'reveal';
    }
  }

  // Get vote tallies
  getVoteTallies(): Record<PlayerId, number> {
    const tallies: Record<PlayerId, number> = {};
    this.snapshot.votes.forEach(vote => {
      tallies[vote.suspectId] = (tallies[vote.suspectId] || 0) + 1;
    });
    return tallies;
  }

  // Get the player(s) with the most votes
  getMostVotedPlayers(): PlayerId[] {
    const tallies = this.getVoteTallies();
    const maxVotes = Math.max(...Object.values(tallies));
    return Object.entries(tallies)
      .filter(([_, votes]) => votes === maxVotes)
      .map(([playerId]) => playerId);
  }

  // Submit imposter's guess for the secret word
  submitImposterGuess(imposterGuess: string): void {
    if (this.snapshot.phase !== 'reveal') {
      throw new Error('Can only guess during reveal phase');
    }
    this.snapshot.imposterGuess = imposterGuess;
  }

  // Reveal the results and calculate scores
  revealAndScore(): void {
    if (this.snapshot.phase !== 'reveal') {
      throw new Error('Not in reveal phase');
    }

    this.snapshot.revealedImposters = true;
    const mostVoted = this.getMostVotedPlayers();

    // Check if the imposter(s) were caught
    const impostersCaught = this.snapshot.imposterIds.some(id => mostVoted.includes(id));

    if (impostersCaught) {
      // Non-imposters get a point
      this.snapshot.players.forEach(playerId => {
        if (!this.snapshot.imposterIds.includes(playerId)) {
          this.snapshot.scores[playerId]++;
        }
      });
    } else {
      // Imposters survived the vote - they get a point
      this.snapshot.imposterIds.forEach(impId => {
        this.snapshot.scores[impId]++;
      });

      // If imposter guessed the word correctly, bonus point
      if (this.snapshot.imposterGuess?.toLowerCase().trim() === this.snapshot.secretWord.toLowerCase().trim()) {
        this.snapshot.imposterIds.forEach(impId => {
          this.snapshot.scores[impId]++;
        });
      }
    }

    this.snapshot.roundsPlayed++;
    this.snapshot.phase = 'finished';
  }

  // Start a new round with new word and imposter
  startNewRound(): void {
    if (this.snapshot.phase !== 'finished') {
      throw new Error('Can only start new round after finishing current round');
    }

    // Determine number of imposters
    const imposterCount = this.snapshot.settings.multipleImposters && this.snapshot.players.length >= 8 ? 2 : 1;
    
    // Randomly select new imposters
    const shuffledPlayers = [...this.snapshot.players].sort(() => Math.random() - 0.5);
    const imposterIds = shuffledPlayers.slice(0, imposterCount);

    // Select a new random word
    const { word, category } = this.selectRandomWord(this.snapshot.settings.category);

    this.snapshot.imposterIds = imposterIds;
    this.snapshot.secretWord = word;
    this.snapshot.category = category;
    this.snapshot.phase = 'dealing';
    this.snapshot.currentRound = 0;
    this.snapshot.clues = [];
    this.snapshot.votes = [];
    this.snapshot.revealedImposters = false;
    this.snapshot.imposterGuess = null;
    this.snapshot.currentPlayerIndex = 0;
  }

  // Add a player (for joining mid-game)
  addPlayer(playerId: PlayerId): void {
    if (this.snapshot.players.includes(playerId)) {
      throw new Error('Player already in game');
    }
    this.snapshot.players.push(playerId);
    this.snapshot.scores[playerId] = 0;
  }

  // Remove a player
  removePlayer(playerId: PlayerId): void {
    this.snapshot.players = this.snapshot.players.filter(p => p !== playerId);
    delete this.snapshot.scores[playerId];
    
    // If removed player was imposter, reassign
    if (this.snapshot.imposterIds.includes(playerId)) {
      const nonImposters = this.snapshot.players.filter(p => !this.snapshot.imposterIds.includes(p));
      if (nonImposters.length > 0) {
        const newImposter = nonImposters[Math.floor(Math.random() * nonImposters.length)];
        this.snapshot.imposterIds = this.snapshot.imposterIds
          .filter(id => id !== playerId)
          .concat(newImposter);
      }
    }
  }

  serialize(): ImposterSnapshot {
    return JSON.parse(JSON.stringify(this.snapshot));
  }

  static fromSnapshot(snapshot: ImposterSnapshot): ImposterGame {
    const game = Object.create(ImposterGame.prototype);
    game.snapshot = JSON.parse(JSON.stringify(snapshot));
    return game;
  }
}

// Helper to get all available categories
export function getAllCategories(): string[] {
  return Object.keys(WORD_CATEGORIES);
}

// Helper to get a specific player's view (hides imposter identity and secret word if they're the imposter)
export function getPlayerView(snapshot: ImposterSnapshot, playerId: PlayerId): Partial<ImposterSnapshot> {
  const isImposter = snapshot.imposterIds.includes(playerId);
  
  return {
    ...snapshot,
    // Imposters don't see the secret word until reveal
    secretWord: isImposter && !snapshot.revealedImposters ? '???' : snapshot.secretWord,
    // Nobody sees who the imposters are until reveal
    imposterIds: snapshot.revealedImposters ? snapshot.imposterIds : [],
  };
}
